import { motion, useReducedMotion } from "framer-motion";
import { Copy, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Stamp } from "@/components/fronk/Stamp";
import { SceneVideo } from "@/components/fronk/SceneVideo";
import { JUPITER_URL, STONKFUN_URL, copyMint, shortMint } from "@/lib/fronk";

export const Hero = () => {
  const reduced = useReducedMotion();

  return (
    <section id="top" className="relative" data-testid="hero-section">
      <SceneVideo
        src="/fronk/motion/fronk-trading-floor-loop.mp4"
        poster="/fronk/hero/fronk-trading-floor-hero.webp"
        posterMobile="/fronk/hero/fronk-trading-floor-mobile.webp"
        eager
        className="min-h-[92svh]"
        alt="FRONK standing on the trading floor while Pepe employees process TOAD payroll"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(14,27,43,0.92) 0%, rgba(14,27,43,0.45) 34%, rgba(14,27,43,0.12) 60%, rgba(14,27,43,0.25) 100%)",
          }}
        />

        <a
          href={JUPITER_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Buy TOAD on Jupiter"
          title="Buy TOAD"
          className={`absolute right-[8%] top-[18%] z-20 hidden w-28 transition-transform duration-300 hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[color:var(--paper-0)] lg:block ${
            reduced ? "" : "fronk-float-slow"
          }`}
          data-testid="hero-toad-capsule-link"
        >
          <img
            src="/fronk/props/pepe-tube-capsule.webp"
            alt="TOAD reward capsule"
            className="w-full opacity-90"
          />
        </a>

        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-6xl px-4 pb-14 sm:px-6 sm:pb-16 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <Stamp data-testid="hero-stamp">Internal memo. All desks.</Stamp>

              <h1 className="font-heading mt-5 max-w-3xl text-4xl font-bold leading-[0.98] tracking-tight text-[color:var(--paper-0)] sm:text-6xl lg:text-7xl">
                THE TOAD PAYROLL DEPARTMENT
              </h1>

              <p className="font-body mt-5 max-w-xl text-base leading-relaxed text-[color:var(--paper-1)] sm:text-lg">
                FRONK is the chairman. The Pepe staff runs the tubes. Every
                transfer clocks in, gets converted into payroll, and eventually
                leaves the mailroom as TOAD for eligible desks.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="rounded-xl bg-[color:var(--stamp-600)] px-6 font-mono-ledger text-sm uppercase tracking-[0.12em] text-[color:var(--paper-0)] shadow-[var(--paper-lift)] transition-colors hover:bg-[color:var(--stamp-500)] active:translate-y-[1px]"
                  data-testid="hero-buy-button"
                >
                  <a href={STONKFUN_URL} target="_blank" rel="noopener noreferrer">
                    Buy $FRONK on StonkFun
                    <ArrowUpRight className="ml-1 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={copyMint}
                  className="rounded-xl border-[color:var(--paper-1)] bg-[rgba(251,247,238,0.12)] px-6 font-mono-ledger text-sm uppercase tracking-[0.12em] text-[color:var(--paper-0)] backdrop-blur-sm transition-colors hover:bg-[rgba(251,247,238,0.24)] hover:text-[color:var(--paper-0)]"
                  data-testid="hero-copy-mint-button"
                >
                  <Copy className="mr-1 h-4 w-4" />
                  Copy mint
                </Button>
              </div>

              <p className="font-mono-ledger mt-6 text-[11px] uppercase tracking-[0.2em] text-[color:var(--brass-200)]">
                Ticker $FRONK. Paired against TOAD. Mint {shortMint}
              </p>
            </motion.div>
          </div>
        </div>
      </SceneVideo>
    </section>
  );
};
